import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminLayout } from "@/components/admin/AdminLayout";
import Dashboard from "./pages/Dashboard";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import AdminAuth from "./pages/admin/Auth";
import Subscriptions from "./pages/Subscriptions";
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import KanbanBoard from "./components/board/KanbanBoard";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import ManageWorkspaces from "./pages/ManageWorkspaces";
import ManageOrganizations from "./pages/ManageOrganizations";
import { AdminDashboard } from "@/components/admin/AdminDashboard";
import { PlansManagement } from "@/components/admin/plans/PlansManagement";
import Organizer from "./pages/Organizer";
import TimeTracking from "./pages/TimeTracking";

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        // Get the last visited path from localStorage, or default to dashboard
        const lastPath = localStorage.getItem('lastPath') || '/dashboard';
        navigate(lastPath);
      } else {
        navigate('/auth');
      }
    });
  }, [navigate]);

  return null;
};

const PathTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Only store protected routes
    if (location.pathname !== '/auth' && location.pathname !== '/') {
      localStorage.setItem('lastPath', location.pathname);
    }
  }, [location]);

  return null;
};

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PathTracker />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/admin/auth" element={<AdminAuth />} />
          
          {/* Protected Admin Routes */}
          <Route path="/admin/*" element={
            <AdminGuard>
              <AdminLayout>
                <Routes>
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="plans" element={<PlansManagement />} />
                  <Route path="customers" element={<div>Customers Management</div>} />
                  <Route path="reports" element={<div>Reports</div>} />
                  <Route path="integrations/shopify" element={<div>Shopify Integration</div>} />
                  <Route path="integrations/ai" element={<div>AI Services</div>} />
                  <Route path="integrations/voice" element={<div>Voice Services</div>} />
                </Routes>
              </AdminLayout>
            </AdminGuard>
          } />

          {/* Protected User Routes */}
          <Route
            path="/dashboard"
            element={
              <AuthGuard>
                <Dashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/manage-workspaces"
            element={
              <AuthGuard>
                <Dashboard>
                  <ManageWorkspaces />
                </Dashboard>
              </AuthGuard>
            }
          />
          <Route
            path="/manage-organizations"
            element={
              <AuthGuard>
                <Dashboard>
                  <ManageOrganizations />
                </Dashboard>
              </AuthGuard>
            }
          />
          <Route
            path="/members"
            element={
              <AuthGuard>
                <Dashboard>
                  <div>Members Management</div>
                </Dashboard>
              </AuthGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <Dashboard>
                  <div>Settings</div>
                </Dashboard>
              </AuthGuard>
            }
          />
          <Route
            path="/time-tracking"
            element={
              <AuthGuard>
                <Dashboard>
                  <TimeTracking />
                </Dashboard>
              </AuthGuard>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <AuthGuard>
                <Subscriptions />
              </AuthGuard>
            }
          />
          <Route
            path="/board/:boardId"
            element={
              <AuthGuard>
                <SubscriptionProvider>
                  <KanbanBoard />
                </SubscriptionProvider>
              </AuthGuard>
            }
          />
          <Route
            path="/organizer"
            element={
              <AuthGuard>
                <Dashboard>
                  <Organizer />
                </Dashboard>
              </AuthGuard>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
